import React, { useState } from 'react';
import PropTypes from 'prop-types';
import TemplatePreview from './TemplatePreview';

// A single pose tile (class-based styles live in StoreGallery's <style> block
// so we get real :hover states). The default background shows through the
// transparent pose cutout; a ♥ toggles favorites.
//
// When a pose is a styled variant (pose.composite present), we render it in the
// browser from the RAW source pose + the LookTemplate spec (TemplatePreview)
// instead of an EC2-baked image -- the POC for lazy, render-on-purchase styling.
const PoseCard = ({
  pose, backgroundUrl, studentName, favorite, layout, onToggleFavorite, onSelect,
}) => {
  if (!pose.ready) {
    return (
      <div className="sg-tile sg-tile--processing">
        <div className="sg-shimmer" />
        <span className="sg-processing">Processing…</span>
      </div>
    );
  }

  const label = pose.number != null ? `Pose ${pose.number}` : 'Photo';
  const imgStyle = backgroundUrl ? { backgroundImage: `url(${backgroundUrl})` } : undefined;
  const [loaded, setLoaded] = useState(false);
  const composite = pose.composite && pose.composite.template ? pose.composite : null;
  const isRail = layout === 'rail';

  // In the rail every tile is a fixed HEIGHT and the look's own aspect sets the
  // width (portrait single vs. landscape collage), so the composite fills the
  // height at its natural width. In the grid it stays boxed in the 4/5 tile.
  let compositeNode = null;
  if (composite) {
    const preview = (
      <TemplatePreview
        template={composite.template}
        photoUrl={composite.source_url}
        backgroundUrl={backgroundUrl}
        studentName={studentName}
        style={isRail ? { height: '100%', width: 'auto', display: 'block' } : undefined}
      />
    );
    // Rail: the composite fills the fixed-height tile at its natural width.
    // Grid: box it in the 4/5 tile so the grid cells stay uniform.
    compositeNode = isRail
      ? preview
      : <div className="sg-img is-loaded" style={{ background: '#fff' }}>{preview}</div>;
  }

  return (
    <div className="sg-tile">
      <button type="button" className="sg-tile-btn" onClick={onSelect} aria-label={`View ${label}`}>
        {composite ? (
          compositeNode
        ) : (
          <img
            src={pose.watermark_url}
            alt={label}
            loading="lazy"
            className={`sg-img${loaded ? ' is-loaded' : ''}`}
            style={imgStyle}
            onLoad={() => setLoaded(true)}
          />
        )}
        <span className="sg-hint">View &amp; shop</span>
      </button>
      <button
        type="button"
        className={`sg-fav${favorite ? ' sg-fav--on' : ''}`}
        aria-label={favorite ? 'Remove from favorites' : 'Add to favorites'}
        aria-pressed={favorite}
        onClick={() => onToggleFavorite(pose.id)}
      >
        {favorite ? '♥' : '♡'}
      </button>
    </div>
  );
};

PoseCard.propTypes = {
  pose: PropTypes.shape({
    id: PropTypes.number,
    number: PropTypes.number,
    ready: PropTypes.bool,
    watermark_url: PropTypes.string,
    composite: PropTypes.shape({
      template: PropTypes.shape({ look_type: PropTypes.string }),
      source_url: PropTypes.string,
    }),
  }).isRequired,
  backgroundUrl: PropTypes.string,
  studentName: PropTypes.string,
  favorite: PropTypes.bool,
  layout: PropTypes.oneOf(['rail', 'grid']),
  onToggleFavorite: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

PoseCard.defaultProps = {
  backgroundUrl: null,
  studentName: null,
  favorite: false,
  layout: 'grid',
};

export default PoseCard;
